import { useEffect, useMemo, useRef } from "react"

import { useQuery } from "@tanstack/react-query"
import { useDispatch, useSelector } from "react-redux"

import { getClientInfo } from "@/services/auth"

import { setUser, logout, userSelector } from "@/slices/user"
import { getToken } from "./useAuthStorage"
import { tenantScopedLocalKey } from "@/utils/tenantStorage"
import { PERMISO_DENEGADO_EVENT } from "@/lib/permisos/puede"

/** Una relectura por 403 cada 30 s como máximo (muchos componentes usan useUser a la vez). */
const RELECTURA_POR_403_MS = 30_000
let ultimaRelecturaPor403 = 0

const useUser = () => {
  const dispatch = useDispatch()

  const storedUser = useSelector(userSelector)

  const { data: queryUser, isLoading, isError, refetch } = useQuery({
    queryKey: ["user-info"],
    queryFn: async () => {
      const response = await getClientInfo();
      const permissions = response.data.permissions || [];

      // Un staff sin perfil (dueño o súper admin) no trae `profile`: no tronar por eso.
      localStorage.setItem(tenantScopedLocalKey("admin"), String(response.data.profile?.id ?? ""))

      const hasCrmRecharge = permissions.includes('crm.user.rechargeFreeFromCrm');
      const hasStripeRecharge = permissions.includes('crm.stripe.initFromWebPortal');

      let selectedPasarela = "";
      if (hasCrmRecharge && hasStripeRecharge) {
        selectedPasarela = "crm.user.rechargeFreeFromCrm";
      } else if (hasCrmRecharge) {
        selectedPasarela = "crm.user.rechargeFreeFromCrm";
      } else if (hasStripeRecharge) {
        selectedPasarela = "crm.stripe.initFromWebPortal";
      }
      if (selectedPasarela) {
        localStorage.setItem(tenantScopedLocalKey("pasarela_recarga"), selectedPasarela);
      } else {
        localStorage.removeItem(tenantScopedLocalKey("pasarela_recarga"));
      }
      // @ts-ignore
      const hasInicio = response.data.menu.some(item => item.name === "Inicio");

      if (hasInicio) {
        localStorage.setItem(tenantScopedLocalKey("inicio"), "true");
      } else {
        localStorage.removeItem(tenantScopedLocalKey("inicio"));
      }

      return response;
    },
    select: (res) => res?.data,
    enabled: !!getToken(),
    // Refetch cada 5 minutos en lugar de cada 10 segundos: el menú/permisos no
    // cambian con frecuencia, refetchear cada 10s rompía el sidebar porque cada
    // refetch reasignaba menu[] con nuevas referencias que invalidaban memos y
    // re-renderizaban toda la UI mientras el usuario interactuaba.
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    // Al volver a la pestaña se relee (si ya pasó 1 min): un cambio de perfil se aplica sin cerrar
    // sesión. El menú tiene referencia estable (abajo), así que releer no re-pinta el sidebar.
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    const relee = () => {
      const ahora = Date.now()
      if (ahora - ultimaRelecturaPor403 < RELECTURA_POR_403_MS) return
      ultimaRelecturaPor403 = ahora
      void refetch()
    }
    window.addEventListener(PERMISO_DENEGADO_EVENT, relee)
    return () => window.removeEventListener(PERMISO_DENEGADO_EVENT, relee)
  }, [refetch])

  // loadingUserInfo solo en carga inicial, no en refetches de fondo
  const hasToken = !!getToken();
  const menuIsEmpty = !storedUser?.menu || storedUser.menu.length === 0;
  const loadingUserInfo = isLoading || (hasToken && menuIsEmpty);
  // ¿Ya se sabe qué pantallas tiene? (a diferencia de loadingUserInfo, un perfil SIN pantallas
  // también queda "listo"). AuthProtected espera a esto antes de pintar una pantalla: pintarla
  // antes disparaba sus consultas y cada carga en frío dejaba 403 aunque luego redirigiera.
  const menuReady = !hasToken || !menuIsEmpty || queryUser !== undefined
  // /me falló y no hay nada guardado: no es "perfil sin pantallas", es que no se pudo saber.
  const menuError = hasToken && menuIsEmpty && queryUser === undefined && isError


  useEffect(() => {
    if (queryUser) {
      dispatch(setUser(queryUser))
    } else if (!loadingUserInfo && !storedUser && !getToken()) {
      dispatch(logout())
    }
  }, [queryUser, storedUser, loadingUserInfo, dispatch])

  // "Acceso total" igual que el candado del middleware: súper admin, dueño o perfil de acceso
  // completo. Antes era `profile.id === 1`, que en cada marca es un perfil distinto. Con un
  // middleware que todavía no manda `profile.is_admin` en /me se conserva el criterio anterior.
  const isAdmin = useMemo(() => {
    const u = storedUser as { is_super_admin?: unknown; is_owner?: unknown; role?: unknown; profile?: { id?: unknown; is_admin?: unknown } } | null
    if (!u) return false
    if (u.is_super_admin || u.is_owner || u.role === "owner_tenant") return true
    const flag = u.profile?.is_admin
    if (flag === undefined || flag === null) return u.profile?.id === 1
    return Number(flag) === 1 || flag === true
  }, [storedUser])

  // Estabilizar la referencia de `menu`: solo cambia cuando el contenido
  // realmente cambia (comparación por JSON serializado, suficiente para árboles
  // de menú pequeños). Sin esto, cada refetch del API reasignaba menu[] con
  // nuevas referencias y forzaba re-render de useMenuItems aunque el contenido
  // fuera idéntico, rompiendo el toggle del sidebar.
  const menuStableRef = useRef<{ key: string; value: any[] }>({ key: "", value: [] })
  const menu = useMemo(() => {
    const current = storedUser?.menu ?? []
    const key = JSON.stringify(current)
    if (key !== menuStableRef.current.key) {
      menuStableRef.current = { key, value: current }
    }
    return menuStableRef.current.value
  }, [storedUser?.menu])

  return {
    user: storedUser ?? null,
    isAdmin,
    loadingUserInfo,
    menuReady,
    menuError,
    retryMenu: refetch,
    menu,
  }
}

// types.ts (o en el mismo archivo)
export interface ApiMenuItem {
  id: number;
  name: string;
  url: string;
  icon: string;
  order: number;
  parent_menu_id: number | null;
  children: ApiMenuItem[];
}

export interface MenuItem {
  id: string;
  isHeader?: boolean;
  label: string;
  icon: string;
  link: string;
  stateVariables: boolean;
  click: React.MouseEventHandler<HTMLAnchorElement>;
  subItems?: MenuItem[];
}

export default useUser
